// Üretim Adımı Onayı penceresi kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'production.confirmStep';

export const productionConfirmStepGuide = (t: TFunction): GuideContent => ({
  layout: 'dialog',
  title: t(`${G}.title`, 'Adım onayı nasıl yapılır?'),
  intro: t(`${G}.intro`, 'Bu pencerede bir üretim adımında yapılan işi kaydedersiniz: kaç sağlam, kaç fire çıktığını, harcanan süreyi ve tüketilen malzemeleri. Kaydettiğiniz anda malzemeler stoktan düşer. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Adım onayı · adım no ve adı'),
    body: t(`${G}.regions.body`, 'Miktar, kontrol, süre, tüketim'),
    footer: t(`${G}.regions.footer`, 'Vazgeç / Kaydet'),
  },
  steps: [
    {
      region: 'body',
      title: t(`${G}.s1.title`, '1. Sağlam ve fire miktarını girin'),
      text: t(`${G}.s1.text`, '"Sağlam" zorunludur; "Fire" 0 gelir. İkisinin toplamı sıfırdan büyük olmalı ve alanın altında yazan "En fazla n" değerini aşmamalıdır. Bu sınır, önceki adımın sağlam miktarından bu adımda daha önce işlenen miktar düşülerek bulunur.'),
      tips: [
        t(`${G}.s1.tip1`, 'Atölye ekranında +1, +10 ve -1 düğmeleriyle sağlam miktarını hızlıca artırıp azaltabilirsiniz.'),
        t(`${G}.s1.tip2`, 'İlk adımda planlanan miktar aşılırsa kayıt yapılır ama "Planlanan miktar aşıldı" uyarısı çıkar.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s2.title`, '2. Adım bitti mi?'),
      text: t(`${G}.s2.text`, 'İş bu adımda tamamen bittiyse "Adımı bitir"i işaretleyin; toplam miktar sınıra ulaşınca kendiliğinden işaretlenir. İşaretlemezseniz adım açık kalır, kalan miktarı sonra yeni bir onayla girersiniz.'),
      tips: [
        t(`${G}.s2.tip1`, 'Önceki adım bitmeden bu adım bitirilemez.'),
        t(`${G}.s2.tip2`, 'Son adım da bitince "Tüm adımlar tamamlandı. Fişi şimdi tamamlamak ister misiniz?" diye sorulur. "Evet" derseniz tamamlama penceresi açılır; mamul stoğa ancak fiş tamamlanınca girer.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s3.title`, '3. Kalite kontrollerini girin'),
      text: t(`${G}.s3.text`, 'Adımda kontrol tanımlıysa "Kalite kontrolleri" bölümü çıkar. Ölçüm alanına değeri yazın, Geçti/Kaldı kontrolünde ilgili düğmeye basın, metin kontrolüne not yazın. Yıldızlı (*) kontroller zorunludur.'),
      tips: [
        t(`${G}.s3.tip1`, '"Adımı bitir" işaretliyken zorunlu bir kontrol girilmemişse ya da "Engelle" ayarlı bir kontrol kaldıysa (ve onaylanmadıysa) adım bitirilemez; eksik/kalan kontrollerin adı yazılır.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. Süreyi kontrol edin'),
      text: t(`${G}.s4.text`, '"Süre" düğmesi "Hazırlık dk" ve "İşleme dk" alanlarını açar. Boş bırakırsanız hazırlık süresi adımın ilk onayında planlanan değer, sonrakilerde 0 alınır; işleme süresi birim süre × (sağlam + fire) olarak hesaplanır.'),
      tips: [
        t(`${G}.s4.tip1`, 'Girilen süreler, istasyonun saatlik makine ve işçilik maliyetiyle çarpılarak fişin gerçek dönüşüm maliyetine eklenir.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s5.title`, '5. Tüketilen malzemeleri kontrol edin'),
      text: t(`${G}.s5.text`, '"Tüketim" bölümü, girdiğiniz miktara göre bu adımda harcanacak malzemeleri önerir. Miktarı, birimi ve depoyu değiştirebilir, satırı silebilir ya da "Malzeme ekle" ile listede olmayan bir malzeme ekleyebilirsiniz; değiştirdiğiniz satırda "elle" yazar.'),
      tips: [
        t(`${G}.s5.tip1`, 'Kırmızı satır ve "Yetersiz" yazısı, eksi stoğa izin verilmeyen bir malzemenin depoda yetmediğini gösterir. Bu durumda kayıt yapılmaz ve "Yetersiz stok" penceresi eksikleri listeler.'),
        t(`${G}.s5.tip2`, 'Lot takibi açıksa masaüstünde Lot sütunundan lot seçebilir ya da "Otomatik (FEFO)" bırakabilirsiniz. Atölye ekranında lot seçilmez; tüketim ilk dolacak son kullanma tarihine göre yapılır.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s6.title`, '6. Operatör, not ve Kaydet'),
      text: t(`${G}.s6.text`, 'Operatörü seçin (ya da adını yazın), gerekirse not ekleyin ve "Kaydet"e basın. Kayıtla birlikte tüketim satırlarındaki malzemeler seçilen depolardan stoktan düşer ve maliyetleri fişe yazılır.'),
      tips: [
        t(`${G}.s6.tip1`, 'Yanlış bir onayı fişin "Operasyonlar" sekmesinden "Geri al" ile geri alabilirsiniz: tüketilen malzemeler stoğa geri alınır. Yalnız adımın son onayı geri alınabilir.'),
        t(`${G}.s6.tip2`, 'Öneri hesaplanırken "Kaydet" kısa bir süre kapalı kalır.'),
      ],
    },
  ],
});
